import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead, { seoConfigs } from '@/components/SEOHead';
import { TrendingUp, TrendingDown, Target, AlertTriangle, RefreshCw, BarChart3, Zap } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Topic label mapping
const TOPIC_LABELS = {
  'Algebra': 'Algebra',
  'AdvancedMath': 'Advanced Math',
  'GeoTrig': 'Geometry & Trig',
  'PSD': 'Problem Solving'
};

// Topic colors for chart
const TOPIC_COLORS = {
  'Algebra': '#3b82f6',
  'AdvancedMath': '#8b5cf6',
  'GeoTrig': '#10b981',
  'PSD': '#f59e0b',
  'Overall': '#ef4444'
};

// Personalized Practice Panel Component
const PersonalizedPracticePanel = ({ analysis, history, loading, onStartTraining, onRefresh }) => {
  const [starting, setStarting] = useState(false);

  const handleStart = async () => {
    setStarting(true);
    try {
      await onStartTraining();
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  // No test data yet
  if (!analysis?.has_data) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8">
          <AlertTriangle size={48} className="mx-auto text-amber-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Test Data Yet</h3>
          <p className="text-gray-600 text-sm mb-4">
            Complete the diagnostic test or a full-length SAT test to unlock personalized practice.
          </p>
          <p className="text-xs text-gray-500">
            We'll analyze your performance to identify areas that need improvement.
          </p>
        </div>
      </div>
    );
  }

  const weaknesses = analysis.weaknesses || [];
  const weakTopics = analysis.weak_topics || [];

  return (
    <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
      {/* Header with refresh */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">Performance Analysis</h2>
        <button
          onClick={onRefresh}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          title="Refresh analysis"
          data-testid="refresh-analysis-btn"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Summary */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4">
        <p className="text-sm text-gray-700">
          Based on <span className="font-semibold">{analysis.total_tests_analyzed}</span> completed test{analysis.total_tests_analyzed !== 1 ? 's' : ''}
        </p>
        {weakTopics.length > 0 ? (
          <p className="text-sm text-red-600 mt-1">
            <AlertTriangle size={14} className="inline mr-1" />
            {weakTopics.length} area{weakTopics.length !== 1 ? 's' : ''} need{weakTopics.length === 1 ? 's' : ''} improvement
          </p>
        ) : (
          <p className="text-sm text-green-600 mt-1">
            All topics above 70% accuracy
          </p>
        )}
      </div>

      {/* Topic Breakdown */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-gray-700">Performance by Topic</h3>
        {weaknesses.map((w) => (
          <div key={w.topic} className="border rounded-lg p-3">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-800">
                {TOPIC_LABELS[w.topic] || w.topic}
              </span>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-bold ${w.is_weak ? 'text-red-600' : 'text-green-600'}`}>
                  {w.accuracy}%
                </span>
                {w.improvement !== null && (
                  <span className={`text-xs flex items-center ${w.improvement >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {w.improvement >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    {Math.abs(w.improvement)}%
                  </span>
                )}
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${w.is_weak ? 'bg-red-500' : 'bg-green-500'}`}
                style={{ width: `${Math.min(w.accuracy, 100)}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>

      {/* Training History Summary */}
      {history?.total_sessions > 0 && (
        <div className="border-t pt-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Practice Progress</h3>
          <div className="flex justify-between text-sm text-gray-600">
            <span>Sessions completed</span>
            <span className="font-semibold">{history.total_sessions}</span>
          </div>
          {history.overall_improvement !== null && (
            <div className="flex justify-between text-sm mt-1">
              <span className="text-gray-600">Overall improvement</span>
              <span className={`font-semibold ${history.overall_improvement >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {history.overall_improvement >= 0 ? '+' : ''}{history.overall_improvement}%
              </span>
            </div>
          )}
        </div>
      )}

      {/* Improvement Over Time Chart */}
      {history?.recent_sessions?.length >= 2 && (
        <div className="border-t pt-4">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 size={16} className="text-indigo-600" />
            <h3 className="text-sm font-medium text-gray-700">Improvement Over Time</h3>
          </div>
          <div style={{ width: '100%', height: 200 }}>
            <ResponsiveContainer>
              <LineChart
                data={(() => {
                  // Prepare chart data from recent sessions (reverse to show oldest first)
                  const sessions = [...(history.recent_sessions || [])].reverse().slice(-10);
                  return sessions.map((session, index) => {
                    const date = new Date(session.completed_at);
                    return {
                      name: `#${index + 1}`,
                      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                      Overall: session.overall_accuracy || 0,
                      Algebra: session.topic_accuracy?.Algebra || 0,
                      'Advanced Math': session.topic_accuracy?.AdvancedMath || 0,
                      'Geometry & Trig': session.topic_accuracy?.GeoTrig || 0,
                      'Problem Solving': session.topic_accuracy?.PSD || 0
                    };
                  });
                })()}
                margin={{ top: 5, right: 5, left: -20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                />
                <YAxis 
                  domain={[0, 100]}
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  axisLine={{ stroke: '#d1d5db' }}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip 
                  contentStyle={{ 
                    fontSize: '12px', 
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px'
                  }}
                  formatter={(value) => [`${value}%`, '']}
                  labelFormatter={(label, payload) => {
                    if (payload && payload[0]) {
                      return payload[0].payload.date;
                    }
                    return label;
                  }}
                />
                <Legend 
                  wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }}
                  iconSize={8}
                />
                <Line 
                  type="monotone" 
                  dataKey="Overall" 
                  stroke={TOPIC_COLORS.Overall}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-gray-400 text-center mt-2">
            Showing last {Math.min(history.recent_sessions?.length || 0, 10)} practice sessions
          </p>
        </div>
      )}

      {/* Start Training Button */}
      <Button
        onClick={handleStart}
        disabled={starting}
        className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white py-3"
        data-testid="start-personalized-practice-btn"
      >
        {starting ? (
          <>
            <RefreshCw size={16} className="animate-spin mr-2" />
            Generating...
          </>
        ) : (
          <>
            <Target size={16} className="mr-2" />
            Start Personalized Practice (10 Questions)
          </>
        )}
      </Button>

      <p className="text-xs text-gray-500 text-center">
        Questions are selected from your weakest topics to maximize improvement
      </p>
    </div>
  );
};

const Practice = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('practice'); // 'practice', 'concept_quiz', 'personalized'
  const [stats, setStats] = useState(null);
  const [streak, setStreak] = useState(null);
  const [weaknessAnalysis, setWeaknessAnalysis] = useState(null);
  const [weaknessLoading, setWeaknessLoading] = useState(false);
  const [trainingHistory, setTrainingHistory] = useState(null);

  // SEO and responsive styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media (min-width: 768px) {
        .mobile-menu-btn { display: none; }
      }
      @media (max-width: 767px) {
        .desktop-nav { display: none; }
        .header-tagline { display: none; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  
  // Filters
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedSubtopic, setSelectedSubtopic] = useState('');
  const topics = [
    // Math Topics
    { value: 'Algebra', label: 'Math: Algebra', section: 'math' },
    { value: 'Advanced Math', label: 'Math: Advanced Math', section: 'math' },
    { value: 'Geometry & Trigonometry', label: 'Math: Geometry & Trigonometry', section: 'math' },
    { value: 'Problem Solving & Data Analysis', label: 'Math: Problem Solving & Data Analysis', section: 'math' },
    // RW Topics
    { value: 'Information and Ideas', label: 'RW: Information and Ideas', section: 'rw' },
    { value: 'Craft and Structure', label: 'RW: Craft and Structure', section: 'rw' },
    { value: 'Expression of Ideas', label: 'RW: Expression of Ideas', section: 'rw' },
    { value: 'Standard English Conventions', label: 'RW: Standard English Conventions', section: 'rw' }
  ];

  useEffect(() => {
    fetchPracticeData();
    fetchWeaknessData();
  }, []);

  const fetchWeaknessData = async () => {
    try {
      setWeaknessLoading(true);
      const [analysisRes, historyRes] = await Promise.all([
        axios.get(`${API}/personalized-practice/analysis`),
        axios.get(`${API}/personalized-practice/history`)
      ]);
      setWeaknessAnalysis(analysisRes.data);
      setTrainingHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching weakness data:', error);
      console.error('Error response:', error.response?.data);
    } finally {
      setWeaknessLoading(false);
    }
  };

  const fetchPracticeData = async () => {
    try {
      setLoading(true);
      const [statsRes, streakRes] = await Promise.all([
        axios.get(`${API}/practice/stats`),
        axios.get(`${API}/practice/streak`)
      ]);
      
      setStats(statsRes.data);
      setStreak(streakRes.data);
    } catch (error) {
      console.error('Error fetching practice data:', error);
      toast.error('Failed to load practice data');
    } finally {
      setLoading(false);
    }
  };

  const startPractice = async () => {
    // Regular practice or concept quiz
    if (!selectedTopic) {
      toast.error('Please select a topic');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/practice/sessions/start`, {
        topic: selectedTopic || 'all',
        subtopic: selectedSubtopic || null,
        difficulty: 'mixed',  // Mixed difficulty - random from all
        mode: mode,
        count: 10  // 10 questions like 4-week plan
      });
      
      if (response.data?.session_id && response.data?.questions?.length > 0) {
        // Navigate with state for PracticeSession component (same as 4-week plan)
        navigate(`/practice-session/${response.data.session_id}`, {
          state: {
            sessionId: response.data.session_id,
            questions: response.data.questions,
            mode: mode,
            topic: selectedTopic || 'all',
            returnPath: '/practice'
          }
        });
      } else {
        toast.error('No questions available for this topic');
      }
    } catch (error) {
      console.error('Error starting practice:', error);
      toast.error(error.response?.data?.detail || 'Failed to start practice');
    }
  };

  const [quickStarting, setQuickStarting] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);

  const quickStart = async (section) => {
    setQuickStarting(section);
    const topicValue = section === 'math' ? 'math_all' : 'rw_all';
    try {
      const response = await axios.post(`${API}/practice/sessions/start`, {
        topic: topicValue,
        subtopic: null,
        difficulty: 'mixed',
        mode: mode,
        count: 10
      });
      if (response.data?.session_id && response.data?.questions?.length > 0) {
        navigate(`/practice-session/${response.data.session_id}`, {
          state: {
            sessionId: response.data.session_id,
            questions: response.data.questions,
            mode: mode,
            topic: topicValue,
            returnPath: '/practice'
          }
        });
      } else {
        toast.error('No questions available right now');
      }
    } catch (error) {
      console.error('Error in quick start:', error);
      toast.error(error.response?.data?.detail || 'Failed to start practice');
    } finally {
      setQuickStarting(null);
    }
  };

  const startQuickReview = async (section) => {
    setReviewLoading(true);
    try {
      const params = new URLSearchParams({ count: 10 });
      if (section) params.append('section', section);
      const response = await axios.get(`${API}/practice/quick-review?${params}`);
      const data = response.data;

      if (!data.session_id || !data.questions?.length) {
        toast.info(data.message || 'No missed questions to review yet. Keep practicing!');
        return;
      }

      navigate(`/practice-session/${data.session_id}`, {
        state: {
          sessionId: data.session_id,
          questions: data.questions,
          mode: 'quick_review',
          topic: 'Quick Review',
          returnPath: '/practice'
        }
      });
    } catch (error) {
      console.error('Error starting quick review:', error);
      toast.error('Failed to start quick review');
    } finally {
      setReviewLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading practice data...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SEOHead {...seoConfigs.practice} />
      <Header 
        variant="authenticated" 
        user={user} 
        setUser={setUser} 
        logoClickPath="/dashboard"
        activePage="practice"
      />

      {/* Main Content */}
      <div style={{ flex: 1, background: '#f5f5f5' }}>
        <div className="dashboard-content">
          {/* Page Title */}
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem', color: '#1a202c', fontFamily: 'Arial, sans-serif' }}>
            SAT Practice
          </h2>
          <p style={{ fontSize: '1rem', color: '#718096', marginBottom: '2rem' }}>
            Improve your skills with targeted practice
          </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Practice Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Mode Selector */}
            <div className="rounded-lg shadow-md p-6" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <h2 className="text-lg font-semibold mb-4" style={{ color: 'white' }}>Practice Mode</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  onClick={() => setMode('practice')}
                  style={{
                    padding: '1.5rem',
                    borderRadius: '0.5rem',
                    border: mode === 'practice' ? '3px solid white' : '2px solid rgba(255,255,255,0.4)',
                    background: mode === 'practice' ? 'white' : 'rgba(255,255,255,0.2)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    flex: 1
                  }}
                  onMouseEnter={(e) => {
                    if (mode !== 'practice') {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (mode !== 'practice') {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                    }
                  }}
                >
                  <div className="text-2xl mb-2">📝</div>
                  <div className="font-semibold" style={{ color: mode === 'practice' ? '#1a202c' : 'white' }}>Practice Questions</div>
                  <div className="text-xs mt-1" style={{ color: mode === 'practice' ? '#718096' : 'rgba(255,255,255,0.8)' }}>Solve practice problems</div>
                </button>
                
                <button
                  onClick={() => {
                    setMode('concept_quiz');
                    // Clear R&W topic selection when switching to Math Concept Quiz
                    const rwTopicValues = topics.filter(t => t.section === 'rw').map(t => t.value);
                    if (rwTopicValues.includes(selectedTopic)) setSelectedTopic('');
                  }}
                  style={{
                    padding: '1.5rem',
                    borderRadius: '0.5rem',
                    border: mode === 'concept_quiz' ? '3px solid white' : '2px solid rgba(255,255,255,0.4)',
                    background: mode === 'concept_quiz' ? 'white' : 'rgba(255,255,255,0.2)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    flex: 1
                  }}
                  onMouseEnter={(e) => {
                    if (mode !== 'concept_quiz') {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (mode !== 'concept_quiz') {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                    }
                  }}
                >
                  <div className="text-2xl mb-2">🧠</div>
                  <div className="font-semibold" style={{ color: mode === 'concept_quiz' ? '#1a202c' : 'white' }}>Math Concept Quiz</div>
                  <div className="text-xs mt-1" style={{ color: mode === 'concept_quiz' ? '#718096' : 'rgba(255,255,255,0.8)' }}>Test your understanding</div>
                </button>
              </div>
            </div>

            {/* Quick Start */}
            {mode === 'practice' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  onClick={() => quickStart('rw')}
                  disabled={quickStarting}
                  data-testid="quick-start-rw-btn"
                  className="flex items-center justify-center gap-2 py-4 px-6 rounded-lg font-semibold text-white shadow-md transition-all hover:shadow-lg active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)' }}
                >
                  {quickStarting === 'rw' ? <RefreshCw size={18} className="animate-spin" /> : <Zap size={18} />}
                  {quickStarting === 'rw' ? 'Loading...' : 'Quick Start — Reading & Writing'}
                </button>
                <button
                  onClick={() => quickStart('math')}
                  disabled={quickStarting}
                  data-testid="quick-start-math-btn"
                  className="flex items-center justify-center gap-2 py-4 px-6 rounded-lg font-semibold text-white shadow-md transition-all hover:shadow-lg active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)' }}
                >
                  {quickStarting === 'math' ? <RefreshCw size={18} className="animate-spin" /> : <Zap size={18} />}
                  {quickStarting === 'math' ? 'Loading...' : 'Quick Start — Math'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => quickStart('math')}
                disabled={quickStarting}
                data-testid="quick-start-math-btn"
                className="w-full flex items-center justify-center gap-3 py-4 px-6 rounded-lg font-semibold text-white shadow-md transition-all hover:shadow-lg active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)' }}
              >
                {quickStarting === 'math' ? <RefreshCw size={18} className="animate-spin" /> : <Zap size={18} />}
                {quickStarting === 'math' ? 'Loading...' : 'Quick Start — All Topics'}
              </button>
            )}

            {/* Quick Review - Missed Questions */}
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-amber-500">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <RefreshCw size={20} className="text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Quick Review</h2>
                  <p className="text-sm text-gray-500">Retry questions you previously got wrong</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => startQuickReview('rw')}
                  disabled={reviewLoading}
                  data-testid="quick-review-rw-btn"
                  className="flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-all"
                >
                  {reviewLoading ? <RefreshCw size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                  Review R&W Mistakes
                </button>
                <button
                  onClick={() => startQuickReview('math')}
                  disabled={reviewLoading}
                  data-testid="quick-review-math-btn"
                  className="flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-all"
                >
                  {reviewLoading ? <RefreshCw size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                  Review Math Mistakes
                </button>
              </div>
            </div>

            {/* Filters - shown for practice and concept_quiz modes */}
            {(mode === 'practice' || mode === 'concept_quiz') && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Practice by Topic</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Topic */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Topic
                  </label>
                  <select
                    value={selectedTopic}
                    onChange={(e) => setSelectedTopic(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="topic-filter-select"
                  >
                    <option value="">Select a Topic</option>
                    {mode === 'practice' && (
                      <optgroup label="Reading & Writing">
                        {topics.filter(t => t.section === 'rw').map((topic) => (
                          <option key={topic.value} value={topic.value}>
                            {topic.label.replace('RW: ', '')}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    <optgroup label="Math">
                      {topics.filter(t => t.section === 'math').map((topic) => (
                        <option key={topic.value} value={topic.value}>
                          {topic.label.replace('Math: ', '')}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>

                {/* Difficulty option removed - similar to concept quiz */}
              </div>

              <button
                onClick={startPractice}
                className="w-full mt-6 bg-indigo-600 text-white py-3 px-4 rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
              >
                Start Practice
              </button>
            </div>
            )}

          </div>

          {/* Performance Dashboard */}
          <div className="lg:col-span-1 space-y-6">
            {/* Streak Display */}
            <div className="bg-white rounded-lg shadow-md" style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', padding: '0.75rem 1.25rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className="text-3xl font-bold text-orange-500">🔥</div>
                <p className="text-xs text-gray-500 mt-1">Streak</p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <p className="text-3xl font-bold text-gray-900">{streak?.current || 0}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Longest: {streak?.longest || 0} days
                </p>
              </div>
            </div>

            {/* Overall Stats */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Overall Performance</h2>
              
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Total Questions</span>
                    <span className="font-semibold text-gray-900">
                      {stats?.total_questions_attempted || 0}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Correct Answers</span>
                    <span className="font-semibold text-green-600">
                      {stats?.total_correct || 0}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Accuracy</span>
                    <span className="font-semibold text-indigo-600">
                      {stats?.overall_accuracy?.toFixed(1) || 0}%
                    </span>
                  </div>
                </div>

                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full transition-all"
                    style={{ width: `${stats?.overall_accuracy || 0}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Topic Stats */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">By Topic</h2>
              
              <div className="space-y-3">
                {Object.entries(stats?.topic_stats || {}).map(([topic, data]) => {
                  const TOPIC_DISPLAY = {
                    'Algebra': 'Algebra',
                    'Advanced Math': 'Advanced Math',
                    'AdvancedMath': 'Advanced Math',
                    'Geometry & Trigonometry': 'Geometry & Trigonometry',
                    'GeoTrig': 'Geometry & Trigonometry',
                    'Geometry': 'Geometry & Trigonometry',
                    'Problem Solving & Data Analysis': 'Problem Solving & Data Analysis',
                    'PSD': 'Problem Solving & Data Analysis',
                    'Standard English Conventions': 'Standard English Conventions',
                    'SEC': 'Standard English Conventions',
                    'Information and Ideas': 'Information and Ideas',
                    'InformationIdeas': 'Information and Ideas',
                    'Craft and Structure': 'Craft and Structure',
                    'CraftStructure': 'Craft and Structure',
                    'Expression of Ideas': 'Expression of Ideas',
                    'ExpressionIdeas': 'Expression of Ideas',
                    'math_all': 'Math (All Topics)',
                    'rw_all': 'Reading & Writing (All Topics)',
                  };
                  const displayName = TOPIC_DISPLAY[topic] || topic;
                  return (
                  <div key={topic} className="border-b border-gray-100 pb-3 last:border-0">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-gray-700">
                        {displayName}
                      </span>
                      <span className="text-sm font-semibold text-indigo-600">
                        {data.accuracy?.toFixed(0)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-indigo-600 h-1.5 rounded-full"
                        style={{ width: `${data.accuracy || 0}%` }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {data.total_attempted} questions attempted
                    </div>
                  </div>
                  );
                })}
                
                {Object.keys(stats?.topic_stats || {}).length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No practice data yet. Start practicing to see your stats!
                  </p>
                )}
              </div>
            </div>

          </div>
        </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Practice;
